import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { ensureUrlEndingWithSlash, i18nIntl, isNotEmpty } from '../../../lib/ep.js';

import ActionLink from '../../builder/ActionLink';
import ActionButton from '../../builder/ActionButton';
import ButtonCollection from '../../builder/ButtonCollection';
import Checkbox from '../../builder/Checkbox';
import Model from '../../builder/Model';

import FloatingLayerPublic from '../FloatingLayerPublic';
import LeveledHTMLText from '../helpers/LeveledHTMLText';

const CookiesAdvise = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.pm ?? false;
	const from_action = props?.from_action ?? false;
	const whole_layout = props?.whole_layout ?? false;
	const ga_params = props?.ga_params ?? {};
	const is_builder = props?.is_builder ?? false;

	const [ display, setDisplay ] = useState(props?.force_view ?? 'advise');
	const [ previous_display, setPreviousDisplay ] = useState('advise');
	const [ user, setUser ] = useState({ cookies_accepted:false });	// by default, analytical_cookies are disabled

	useEffect(() => {
		if((props?.force_view ?? display) != display) {
			setDisplay(props.force_view);
		}
	}, [ props.force_view ]);

	if(promotion === false) return null;

	const change = (k, v) => {
		setUser({ ...user, [k]:v });
	};

	const acceptCookies = (force_value) => {
		if(is_builder) {
			if(props.display_feedback_message != undefined) props.display_feedback_message(user.cookies_accepted);
			return;
		}

		EP.cookies.acceptCookies((force_value != undefined) ? force_value : user.cookies_accepted, ga_params);
		setDisplay(false);
	};

	const closeLayer = () => {
		if(props.force_close != undefined) {
			props.force_close();
			return;
		}

		changeDisplay((display == 'legal_text') ? previous_display : 'advise');
	};

	const changeDisplay = (new_display) => {
		setPreviousDisplay(display);
		setDisplay(new_display);
		externalDisplayChange(new_display);
	};

	const externalDisplayChange = (new_display) => {
		if(props.action_after_change_view == undefined) return null;

		const returned_display = (new_display !== false) ? new_display : 'advise';
		props.action_after_change_view(returned_display);
	};

	const default_cookies_text = i18n("<p>The promotion takes place on a website that uses cookies and other files with similar functions. Cookies are small data files sent to the user's computer, smartphone or other access device when the user visits a website. Cookies provide us with information concerning the user's browsing habits on the website.</p><br /><ol><li><strong>Cookies installed in this promotion:</strong><ol><li><strong>Technical cookies</strong><p>Some cookies are used internally and are essential for the proper functioning of the promotion. These include cookies that register whether a user has already participated or not; cookies that monitor and prevent fraud in voting contests; cookies that temporarily save questionnaire responses; and cookies that detect whether a user has been invited by another participant. These cookies are saved only for the duration of one session and are automatically deleted when users close their browsers.</p><br /><p>Blocking these cookies precludes the proper functioning of the promotion.</p><br /><p>These cookies can be identified by the name *_ep_requests y PHPSESSION.</p></li><br /><li><strong>Analytical cookies</strong><p>Analytical cookies are used to collect statistics on the activity of promotion users. The information collected provides us with information such as the number of pages visited; the languages used; the frequency and repetition of visits; the time of visits; the browser used; the referral traffic source, and so on. Users can exclude their individual activity by means of the exclusion systems provided by the analytical tools or by way of their own browser settings.</p><br /><p><a href='https://analytics.google.com/analytics/web/#/' target='_blank'>GoogleAnalytics</a> cookies are used as the promotion's analytical system.</p></li><br /><li><strong>Social media cookies.</strong><p>The promotion incorporates spaces and contents which contain social media buttons that enable users to share the promotion across the social networks.</p><br /><p>We have no control over the cookies installed by each social network. However, they tend to be technical cookies, used to control the start of the session, or to display a counter showing how many times a certain piece of content has been shared.</p><br /><p>We recommend that all users review the privacy and cookie policy of each social network, and if necessary, modify the privacy parameters of each network to limit any tracking that they may attempt to carry out.</p></li></ol></li><br /><li><strong>Deactivate or delete cookies.</strong><p>Most web browsers allow users to manage, at any time, their cookies preferences. Users can adjust their browser settings to block or delete certain cookies if desired.</p><br /><p>To manage cookie settings, go to:<ul><li><a href='https://support.google.com/chrome/answer/95647?co=GENIE.Platform&&hl=en' target='_blank'>Google Chrome</a></li><li><a href='https://support.mozilla.org/en-US/kb/block-websites-storing-site-preferences' target='_blank'>Mozilla Firefox</a></li><li><a href='https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies' target='_blank'>Internet Explorer</a></li><li><a href='https://privacy.microsoft.com/en-US/windows-10-microsoft-edge-and-privacy' target='_blank'>Microsoft Edge</a></li><li><a href='https://support.apple.com/en-us/HT201265' target='_blank'>Safari</a></li></ul></p><br /><p>Users can revoke consent for use of cookies in their web browsers by means of the indications above.</p><br /><p>Users should bear in mind that some features and functionalities of the promotion will only work if cookies are enabled in their web browsers. If the user decides not to accept or to block certain cookies (depending on their purpose), this may affect, totally or partially, the user's participation in the promotion.</p></li></ol>");

	const cookies_legal_text = (isNotEmpty(promotion, 'cookies')) ? i18n(promotion.cookies) : default_cookies_text;

	if(whole_layout === false && display === false) return null;
	if(cookies_legal_text === false) return null;

	let layer_title = "Cookie notice";
	if(display == 'settings') layer_title = "Cookie manager";
	if(display == 'legal_text') layer_title = "Cookie policy";

	const cookies_advise_text = (isNotEmpty(promotion, 'cookies_advise')) ? promotion.cookies_advise : i18n("We use technical cookies for the promotion to work and for you to participate in it, as well as analytical cookies for statistical information. You can disable analytical cookies from the reject button. Check our {link} for more information.").replace("%s", "{link}");

	const cookies_setup_text = (isNotEmpty(promotion, 'cookies_settings_msg')) ? promotion.cookies_settings_msg : i18n("<p>When you visit a website, it can store or retrieve information in your browser, mainly in the form of cookies. 2 types of cookies are used for this promotion:</p><p><strong>Technical cookies</strong></p><p>Technical cookies are introduced by Easypromos, which is the program used for the development of this campaign. These cookies are necessary for users to participate: fraud control, participation control, customize the interface according to the user, etc. We include social media cookies as technical cookies, as they are necessary to offer the sharing and social login functionality.</p><p>They are mandatory. If you reject them from the browser options, you will not be able to participate in the promotion.</p><p><strong>Analytical cookies</strong></p><p>These cookies allow us to know the total number of visits to the promotion, the user interaction experience and quantify the performance of the campaign. For this, we use Google Analytics cookies.</p>");

	const enable_close = (display != 'advise' || from_action) ? true : false;

	let public_url = window?.common?.public_url ?? (window?.public_url ?? (window?.common?.domain ?? window.domain_url));
	if(public_url.indexOf("http") == -1) public_url = `https://${public_url}`;

	const generic_cookies_link = `${ensureUrlEndingWithSlash(public_url)}cookies/${promotion.id}`;

	const external_cookies_url = (isNotEmpty(promotion, 'external_cookies_url')) ? promotion.external_cookies_url : false;

	return (
		<FloatingLayerPublic
			pm={promotion}
			enable_close={(enable_close) ? true : false}
			backButton={(enable_close) ? () => closeLayer() : null}
			title={`<h1>${i18n(layer_title)}</h1>`}
			enable_behavior={true}
			is_builder={is_builder}
		>
			{(display == 'advise') && (
				<div>
					{(cookies_advise_text != " ") && (
						<LeveledHTMLText
							_class={{ other:'gui_justified_text' }}
						>
							<FormattedMessage
								id={cookies_advise_text}
								values={{
									link:<ActionLink
										_href={(!is_builder && external_cookies_url !== false) ? external_cookies_url : null}
										_target={(!is_builder && external_cookies_url !== false) ? '_blank' : null}
										onClick={(!is_builder && external_cookies_url === false) ? () => changeDisplay('legal_text') : null}
										autotranslate={false}
									>{i18n("Cookie policy", 'lowercase')}</ActionLink>,
									manage_cookies:<ActionLink
										onClick={(!is_builder) ? () => changeDisplay('settings') : null}
										autotranslate={false}
									>{i18n("Cookie manager", 'lowercase')}</ActionLink>,
								}}
							/>
						</LeveledHTMLText>
					)}

					<div class="gui_not_in_top_big asided_footer_buttons">
						<ButtonCollection
							_class={{ other:'flex_button adaptable_button' }}
						>
							<ActionButton
								_fullclass="submit_alike"
								onClick={(!is_builder) ? () => acceptCookies(true) : null}
							>Accept</ActionButton>

							<ActionButton
								_fullclass="submit_alike"
								onClick={(!is_builder) ? () => acceptCookies(false) : null}
							>Reject</ActionButton>
						</ButtonCollection>
					</div>
				</div>
			)}

			{(display == 'settings') && (
				<div>
					{(cookies_setup_text != " ") && (
						<LeveledHTMLText
							_class={{ other:'gui_justified_text' }}
						>{cookies_setup_text}</LeveledHTMLText>
					)}

					<Model classname="gui_form gui_not_in_top_small" model={user} change={change}>
						<Checkbox
							name="cookies_accepted"
							_class={{ first:true }}
						>Enable analytical cookies</Checkbox>
					</Model>

					{(!whole_layout) && (
						<LeveledHTMLText>
							<p>
								<FormattedMessage
									id="For more information see our {link}."
									values={{
										link:<ActionLink
											onClick={(!is_builder) ? () => changeDisplay('legal_text') : null}
											autotranslate={false}
										>{i18n("Cookie policy", 'lowercase')}</ActionLink>,
									}}
								/>
							</p>
						</LeveledHTMLText>
					)}

					<ButtonCollection _class={{other:'gui_not_in_top'}}>
						<div class="flex_button adaptable_button">
							<ActionButton
								_fullclass="submit_alike"
								onClick={(!is_builder) ? () => acceptCookies() : null}
							>Save</ActionButton>
						</div>
					</ButtonCollection>
				</div>
			)}

			{(display == 'legal_text') && (
				<div>
					<LeveledHTMLText
						_class={{ other:'gui_justified_text' }}
					>{cookies_legal_text}</LeveledHTMLText>

					<LeveledHTMLText
						_class={{ other:'gui_not_in_top_small' }}
					>
						<p>
							<FormattedMessage
								id="Configure the promotions cookies from the {link}."
								values={{
									link:<ActionLink
										onClick={(!is_builder) ? () => changeDisplay('settings') : null}
										autotranslate={false}
									>{i18n("Cookie manager", 'lowercase')}</ActionLink>,
								}}
							/>
						</p>
					</LeveledHTMLText>

					<ul class="related_link original_color_item">
						<li class="link">
							<ActionLink
								_href={(!is_builder) ? generic_cookies_link : null}
								_target='_blank'
								_class={{ other:'toc_link' }}
								autotranslate={false}
							>{generic_cookies_link}</ActionLink>
						</li>
					</ul>
				</div>
			)}
		</FloatingLayerPublic>
	);
}

export default injectIntl(CookiesAdvise);